import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { BaseSiteService, UserIdService } from '@spartacus/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, retry, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ADDRESS_NAME, SnahCountryService } from '../snah/snah-countries.service';

export interface AddressWsDTO {
  firstName?: string;
  lastName?: string;
  email?: string;
  cellphone?: string;
  country: { isocode: string };
  line1: string;
  line2: string;
  postalCode: string;
  town: string;
  region?: {
    countryIso: string;
    isocode: string;
  };
}

export interface DefaultAddressPayload {
  addressId: string;
}

export interface RemoveAddressPayload {
  addressId: string;
}

export interface RequestDeliveryAddrWsDTO {
  firstName: string;
  lastName: string;
  country: {
    isocode: string;
    name: string;
  };
  line1: string;
  line2?: string;
  postalCode: string;
  town: string;
  region?: {
    countryIso: string;
    isocode: string;
  };
  district?: string;
}

export interface BillingAddressWsDTO {
  firstName?: string;
  lastName?: string;
  district?: string;
  country?: { isocode?: string };
  region?: { countryIso?: string; isocode?: string; isocodeShort?: string; name?: string };
  line1?: string;
  line2?: string;
  postalCode?: string;
  town?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MyaccountPersonalInfoService {
  baseSite$: Observable<string> = this.baseService.getActive();
  occURL = this.meta.getTag('name=occ-backend-base-url');
  userCurrent$: Observable<string> = this.auth.getUserId();
  url = `${this.occURL?.content}${environment.prefix}${this.baseSiteID}`;
  headers = new HttpHeaders().set('Content-Type', 'application/json');
  baseSiteId: any;
  showAddressName$: Observable<boolean> = this.snahCountryService.getSnahFeatures(ADDRESS_NAME);
  constructor(
    protected http: HttpClient,
    protected auth: UserIdService,
    protected baseService: BaseSiteService,
    private meta: Meta,
    private snahCountryService: SnahCountryService
  ) {
    this.baseSite$.pipe(take(1)).subscribe((data) => {
      this.baseSiteId = data;
    });
  }

  get baseSiteID(): string {
    let baseSiteId = '';
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }

  get userCurrent(): string {
    let userCurrent: string;
    this.userCurrent$.pipe(take(1)).subscribe((userId) => (userCurrent = userId));
    return userCurrent;
  }

  getPersonalInfo() {
    return this.http
      .get(`${this.occURL?.content}${environment.prefix}${this.baseSiteId}/units/${this.userCurrent}/personalInfo`, {})
      .pipe(map((data) => data));
  }

  postPersonalInfo(payload: AddressWsDTO): Observable<any> {
    return this.http
      .post(
        `${this.occURL?.content}${environment.prefix}${this.baseSiteId}/units/${this.userCurrent}/personalInfo`,
        payload,
        {}
      )
      .pipe(catchError(this.errorHandler));
  }

  postRequestDeliveryAddrForm(formDetails: RequestDeliveryAddrWsDTO): Observable<any> {
    const httpOptions = {
      headers: this.headers,
      observe: 'response' as 'response',
    };
    return this.http
      .post(
        `${this.occURL?.content}${environment.prefix}${this.baseSiteId}/units/${this.userCurrent}/deliveryAddress`,
        formDetails,
        httpOptions
      )
      .pipe(catchError(this.errorHandler));
  }

  getAddress(): Observable<any> {
    return this.http
      .get(`${this.occURL?.content}${environment.prefix}${this.baseSiteId}/units/${this.userCurrent}/unitAddresses`, {})
      .pipe(map((data) => data));
  }

  removeAddress(payload: RemoveAddressPayload) {
    const params = new HttpParams().set('addressId', payload.addressId as any);
    return this.http
      .post(
        `${this.occURL?.content}${environment.prefix}${this.baseSiteId}/units/${this.userCurrent}/removeDeliveryAddress`,
        null,
        { params }
      )
      .pipe(catchError(this.errorHandler));
  }

  setDefaultAddress(payload: DefaultAddressPayload) {
    const params = new HttpParams().set('addressId', payload.addressId as any);
    return this.http
      .post(
        // tslint:disable-next-line: max-line-length
        `${this.occURL?.content}${environment.prefix}${this.baseSiteId}/units/${this.userCurrent}/defaultDeliveryAddress`,
        null,
        { params }
      )
      .pipe(catchError(this.errorHandler));
  }

  setBillingAddress(payload: BillingAddressWsDTO) {
    return this.http
      .post(`${this.url}/units/${this.userCurrent}/billingAddress`, JSON.stringify(payload), {
        headers: this.headers,
      })
      .pipe(map((data) => data));
  }

  formatAddress(addresses: any): Observable<string> {
    return this.showAddressName$.pipe(
      take(1),
      map((showAddressName: boolean) => {
        if (showAddressName && addresses.addressName) {
          return `<span class="address-name">${addresses?.addressName}: </span>${addresses?.formattedAddress}`;
        } else {
          return addresses.formattedAddress;
        }
      })
    );
  }

  getBillingEditStatus(): Observable<any> {
    return this.http
      .get(`../../../public/features/MYACCOUNT_BILLING_FEATURES.json`)
      .pipe(retry(1), catchError(this.errorHandler));
  }

  errorHandler(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(errorMessage);
  }
}
